import { useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useIsIOS } from '@shared/utils/media/useMediaScreens';
import type { ShopCurrencyWarningDialogProps } from './ShopCurrencyWarningDialog';
import { config } from '@static/js/env.config';
import { useShippingAddressTelemetry } from '@apps/registry/common/components/ShippingAddressAndCurrencyDialogsProvider/ShippingAddress.telemetry';

export const useShopCurrencyWarningDialogController = ({ onClose, showInstallBrowserButton, onAddGift, onAddAnyway, eventId }: ShopCurrencyWarningDialogProps) => {
  const { t2 } = useTranslation('catalogRegistry');
  const { title, subtitle, mobileSubtitle, ctaOne, ctaOneButton, ctaTwo, ctaTwoButton, mobileButton, dismiss, addAnyway } = t2('currencyWarningDialog');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const isIOS = useIsIOS();

  const { shopCurrencyWarningDialogButton } = useShippingAddressTelemetry({ addToContext: { eventId } });

  const onShowAddGiftDialog = useCallback(() => {
    onClose();
    shopCurrencyWarningDialogButton('Add Item Manually');
    onAddGift?.();
  }, [onClose, shopCurrencyWarningDialogButton, onAddGift]);

  const onShowInstallBrowserButton = useCallback(
    (e: React.MouseEvent) => {
      onClose();
      shopCurrencyWarningDialogButton('Install Browser Button');
      showInstallBrowserButton?.(e);
    },
    [onClose, shopCurrencyWarningDialogButton, showInstallBrowserButton],
  );

  const handleOnAddAnyway = useCallback(() => {
    shopCurrencyWarningDialogButton('Add Item Anyway');
    onAddAnyway();
    onClose();
  }, [onAddAnyway, onClose, shopCurrencyWarningDialogButton]);

  const getAppStoreLink = () => {
    return `${config.clientUri}/getJoy?src=admin&dest=${isIOS ? 'ios' : 'android'}`;
  };

  return {
    onShowAddGiftDialog,
    onShowInstallBrowserButton,
    getAppStoreLink,
    isMobile,
    handleOnAddAnyway,
    translations: {
      title,
      subtitle,
      mobileSubtitle,
      ctaOne,
      ctaOneButton,
      ctaTwo,
      ctaTwoButton,
      mobileButton,
      dismiss,
      addAnyway,
    },
  };
};
